// Login.tsx
// Page de connexion au compte Qollect.
// Updated: suppression des placeholders, ajout du lien "Mot de passe oublié",
// refactoring vers prop loading du Button, corrections visuelles (font-bold, taille bouton, couleur lien).
// Updated: lien "Mot de passe oublié" rendu discret (neutral-500), remplacement Input par PasswordInput.
// Updated: lit le query param ?redirect= pour renvoyer l'utilisateur vers la destination
//   prévue après connexion (ex: /clients depuis un email de notification).
// Updated: getSafeRedirect validates the redirect param to prevent open-redirect attacks;
//   only relative paths starting with a single '/' are accepted, everything else falls back to /dashboard.
// Updated 2026-04-07: remplacement du titre texte "Qollect" par le logo SVG.

import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PasswordInput from '../components/ui/PasswordInput';
import Card from '../components/ui/Card';

function getSafeRedirect(raw: string | null): string {
  if (!raw) return '/dashboard';
  if (!raw.startsWith('/')) return '/dashboard';
  if (raw.startsWith('//')) return '/dashboard';
  if (/^\/[a-zA-Z][a-zA-Z\d+\-.]*:/i.test(raw)) return '/dashboard';
  return raw;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectTo = getSafeRedirect(searchParams.get('redirect'));

  useEffect(() => {
    if (user) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      setLoading(false);
      return;
    }

    const { error: signInError } = await signIn(email, password);

    if (signInError) {
      setError('Email ou mot de passe incorrect');
      setLoading(false);
      return;
    }

    navigate(redirectTo, { replace: true });
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-soft-xl">
        <div className="text-center mb-10">
          <img src="/logo-qollect.svg" alt="Qollect" className="h-[50px] w-auto mx-auto mb-4" />
          <p className="text-neutral-500 text-lg">Connectez-vous à votre compte</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl font-medium">
              {error}
            </div>
          )}

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />

          <div className="space-y-1.5">
            <PasswordInput
              label="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </div>

          <Button
            type="submit"
            fullWidth
            size="md"
            loading={loading}
          >
            Se connecter
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-neutral-600">
            Vous n'avez pas encore de compte ?{' '}
            <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Créer un compte
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}
